import { phoneCodes } from '../data/phone-codes'
import { FLAG_OTHER } from '../components/field/tel-flags'

const defaultMask = phoneCodes.find(phoneCode => phoneCode.cc === 'RU') || phoneCodes[0]
const countriesList = ['KZ', 'AM', 'BY', 'KG', 'AZ', 'GE', 'MD', 'TJ', 'TM', 'UZ', 'UA', 'RU']

/**
 * Возвращает объект с данными о подходящей телефонной маске по ISO коду страны.
 * @param {string} countryCode Двухбуквенный ISO код страны.
 * @returns {Object} Объект с данными о подходящей телефонной маске.
 */
export const getCountryInfo = countryCode => {
  const foundMask = phoneCodes.find(phoneCode => phoneCode.cc === countryCode)
  return foundMask || defaultMask
}

/**
 * Возвращает объект с данными о подходящей телефонной маске по номеру телефона.
 * @param {string} phoneNumber Номер телефона.
 * @param {Object} options Дополнительные параметры.
 * @returns {Object} Объект с данными о подходящей телефонной маске.
 */
export const getCountryPhoneMask = (phoneNumber, options) => {
  const { fallbackCountryCode } = options || {}
  const phoneNumberPart = preparePhoneNumber(phoneNumber).slice(0, 3) // убираем все символы кроме цифр
  // находим информацию о странах, для которых нужны маски
  const masks = phoneCodes.filter(phoneCode => countriesList.includes(phoneCode.cc))
  const foundMask = masks.find(phoneCode =>
    // если есть подмаски, проверяем по ним. если нет, проверяем по коду страны
    Array.isArray(phoneCode.submasks)
      ? phoneCode.submasks.some(submask => _isStartsWith(phoneNumberPart, submask))
      : _isStartsWith(phoneNumberPart, phoneCode.countryCode)
  )

  return foundMask || getCountryInfo(fallbackCountryCode)
}

/**
 * Возвращает булево значение, если строка содержит подмаску страны.
 * @param {string} str Первые три цифры телефона.
 * @param {string} substr Подмаска телефона страны.
 * @returns {boolean} Булево значение, определяющее соответствие str и substr.
 */
export const _isStartsWith = (str, substr) => {
  return str.indexOf(substr) === 0
}

/**
 * Возвращает отформатированный номер
 * @param {string} number номер телефона
 * @param {boolean} withSpaces добавить пробелы в маске
 * @returns {string} отформатированный по маске номер
 */
export const getFormattedPhoneNumber = (number, withSpaces = false) => {
  let phoneNumber,
    maskObj
  // Оставляем только цифры
  phoneNumber = preparePhoneNumber(number)
  if (phoneNumber.charAt(0) === '8') {
    phoneNumber = `7${phoneNumber.substr(1)}`
  }
  maskObj = getCountryPhoneMask(phoneNumber, { fallbackCountryCode: FLAG_OTHER })

  if (!maskObj) {
    return number
  }

  return _applyMask(phoneNumber, maskObj, withSpaces)
}

/**
 * Применяет маску к номеру
 * @param {string} phoneNumber - номер, к которому будет применяться маска
 * @param {Object} maskObj - обьект с данными маски
 * @param {boolean} withSpaces добавить пробелы в маске
 * @returns {string}
 * @private
 */
const _applyMask = (phoneNumber, maskObj, withSpaces = false) => {
  const placeholder = '#'
  let reg,
    digitsArray,
    numberOfDigits,
    resultArr,
    phoneNumberArray,
    mask = maskObj.mask.replace(/\\/g, ''),
    countryCode = maskObj.countryCode

  if (!phoneNumber || !maskObj) {
    return ''
  }
  if (withSpaces) {
    mask = mask.replace(/\(/, '\u00a0(').replace(/\)/, ')\u00a0')
  }
  mask = mask.substr(0, countryCode.length + 1) + mask.substr(countryCode.length + 1).replace(/[9]/g, placeholder)
  // Считаем количество цифр в маске
  reg = new RegExp('[0-9' + placeholder + ']', 'g')
  digitsArray = mask.match(reg)
  numberOfDigits = digitsArray ? digitsArray.length : 0

  /*
   * Разбиваем маску и номер на массив и разворачиваем,
   * чтобы подставлять номер в маску с конца
   */
  resultArr = mask.split('').reverse()
  phoneNumberArray = phoneNumber.split('').reverse()

  for (let i = 0; i < resultArr.length; i++) {
    if (phoneNumberArray.length) {
      // Заменяем символы маски на цифры номера
      if (resultArr[i] === placeholder) {
        /*
         * Если в маске остался последний плейсхолдер, а в номере больше одной цифры,
         * заменяем плейсхолдер на оставшивеся цифры номера и убираем оставшиеся символы в маске.
         */
        if (numberOfDigits === 1 && phoneNumberArray.length > 1) {
          resultArr[i] = phoneNumberArray.reverse().join('')
          resultArr.splice(i + 1, resultArr.length)
        } else {
          resultArr[i] = phoneNumberArray.shift()
          numberOfDigits--
        }
      }
    } else {
      /*
       * Если цифры номера кончились, но маска ожидает еще цифры,
       * удаляем оставшиеся символы. В противном случае выходим из цикла не меняя результат.
       */
      if (resultArr.slice(i).join('').match(reg)) {
        resultArr.splice(i, resultArr.length)
      } else {
        break
      }
    }
  }

  return resultArr.reverse().join('')
}

/**
 * Заменяет 89 на 79, если номер соответствует российскому.
 * @param {string} phoneNumber Текущее состояние.
 * @returns {string} Обработанный номер телефона.
 */
export const getLocalPhoneNumber = phoneNumber => {
  const replacedPhone = preparePhoneNumber(phoneNumber)
  if (replacedPhone.length === 11 && replacedPhone.slice(0, 2) === '89') {
    return `7${replacedPhone.substr(1)}`
  }
  return phoneNumber
}

/**
 * Подготавливает номер телефона исключая все нечисловые значения.
 * @param {string} phoneNumber Номер телефона
 * @returns {string} Подготовленный номер телефона.
 */
export const preparePhoneNumber = phoneNumber => {
  return String(phoneNumber).replace(/\D/g, '')
}
